import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { makeCustomDecimalNumberMask } from '@app/functions';
import {
    capOnOptions,
    CapOnOptions,
    carryoverSubTypes,
    CarryoverSubTypes,
    carryoverTypes,
    CarryoverTypes,
} from '@app/modules/time-off-v3/types/carryover-card.type';
import { Locale } from '@app/types/translatable.type';
import { TimeOffPolicy } from '@models/time-off-v3/time-off-policy.model';
import { TranslateService } from '@ngx-translate/core';
import {
    END_OF_MONTH,
    MonthlyIntervals,
    MonthsForDropdown,
    TimeOffDateFormatDashes,
} from '@time-off-v3/meta/time-off-meta';
import moment from 'moment';

@Component({
    selector: 'app-carryover-update-card',
    templateUrl: './carryover-update-card.template.html',
    styleUrls: ['./carryover-update-card.style.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class CarryoverUpdateCardComponent implements OnInit {
    @Input() timeOffPolicy = new TimeOffPolicy();
    @Output() onChange = new EventEmitter();
    isLoading = false;

    daysInMonth = MonthlyIntervals;
    months = MonthsForDropdown;
    capOnDateVal: CapOnOptions;
    capOnOptions = capOnOptions;
    carryoverTypes = carryoverTypes;
    carryoverSubTypes = carryoverSubTypes;

    carryoverMonth = 0;
    carryoverDay = 1;

    topLevelCarryoverSelection: CarryoverTypes = carryoverTypes.annualReset;
    subLevelCarryoverSelection: CarryoverSubTypes = carryoverSubTypes.limitedCarryover;

    numberMask = makeCustomDecimalNumberMask(this.translateService.currentLang as Locale);

    constructor(private translateService: TranslateService) {}

    ngOnInit(): void {
        this.setCarryoverMonthAndDay();

        if (!this.timeOffPolicy.isAnnualReset()) {
            this.topLevelCarryoverSelection = carryoverTypes.carryOver;
        }

        if (this.timeOffPolicy.isUnlimitedCarryover()) {
            this.subLevelCarryoverSelection = carryoverSubTypes.unlimitedCarryover;
        }

        this.capOnDateVal = this.timeOffPolicy.getCapOnDateOption();
    }

    topLevelCarryoverSelectionChanged(): void {
        if (this.topLevelCarryoverSelection === carryoverTypes.annualReset) {
            this.timeOffPolicy.carryoverCap = 0;
            this.timeOffPolicy.carryoverExpireAfter = null;
            this.timeOffPolicy.carryoverDate = this.getStartOfNextYearFormatted();

            this.onChange.emit();

            return;
        }

        // carryover type
        this.subLevelCarryoverSelectionChanged();
    }

    subLevelCarryoverSelectionChanged(): void {
        if (this.subLevelCarryoverSelection === carryoverSubTypes.unlimitedCarryover) {
            this.timeOffPolicy.carryoverCap = null;
            this.timeOffPolicy.carryoverExpireAfter = null;
            this.timeOffPolicy.carryoverDate = null;
            this.timeOffPolicy.carriesOverOnHireDate = false;

            this.onChange.emit();

            return;
        }

        // set to limited carryover
        this.timeOffPolicy.carryoverCap = this.timeOffPolicy.carryoverCapOriginal;
        this.timeOffPolicy.carryoverExpireAfter = this.timeOffPolicy.getOriginalAttribute('carryoverExpireAfter');
        this.timeOffPolicy.carryoverDate =
            this.timeOffPolicy.getOriginalAttribute('carryoverDate') ?? this.getStartOfNextYearFormatted();
        this.setCarryoverMonthAndDay();

        this.onChange.emit();
    }

    updateCarryoverDate(): void {
        this.onChange.emit();
        this.timeOffPolicy.carryoverDate = moment()
            .month(this.carryoverMonth)
            .date(
                this.carryoverDay === END_OF_MONTH
                    ? moment().month(this.carryoverMonth).endOf('month').date()
                    : this.carryoverDay
            )
            .format(TimeOffDateFormatDashes);
    }

    capOnDateChange(): void {
        switch (this.capOnDateVal) {
            case capOnOptions.yearEnd:
                this.timeOffPolicy.carriesOverOnHireDate = false;
                this.timeOffPolicy.carryoverDate = this.getStartOfNextYearFormatted();
                this.onChange.emit();
                break;
            case capOnOptions.dateHired:
                this.timeOffPolicy.carriesOverOnHireDate = true;
                this.timeOffPolicy.carryoverDate = null;
                this.onChange.emit();
                break;
            case capOnOptions.pickDate:
                this.timeOffPolicy.carriesOverOnHireDate = false;
                this.onChange.emit();
                break;
        }
    }

    private setCarryoverMonthAndDay(): void {
        const carryover = this.getCarryoverDateAsMoment();

        if (carryover) {
            this.carryoverMonth = carryover.month();

            const isEndOfMonth = carryover.isSame(moment().month(carryover.month()).endOf('month'), 'day');

            this.carryoverDay = isEndOfMonth ? -1 : carryover.date();
        }
    }

    private getCarryoverDateAsMoment(): moment.Moment | null {
        if (!this.timeOffPolicy.carryoverDate) {
            return null;
        }

        return moment(this.timeOffPolicy.carryoverDate, TimeOffDateFormatDashes);
    }

    private getStartOfNextYearFormatted(): string {
        return moment().startOf('year').add(1, 'y').format(TimeOffDateFormatDashes);
    }
}
